import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import fileDownload from "js-file-download";
import {
  setGDDKFilterStation,
  setGDDKFilterResourceType,
  setGDDKFilterGDDKPeriod,
  getActiveGDDKListRequest,
  setHistoryGDDKFilterResourceType,
  setHistoryGDDKFilterGDDKPeriod,
  getGDDKHistoryListRequest,
} from "../../../redux/actions/gddk";
import { convertAmountToFrontend } from "../../../helpers/convert";
import ComboBox from "../../../components/ComboBox";
import ComboBoxStation from "../../../components/ComboBoxStation";
import { Input, Select } from "../../../components/Inputs";
import Button from "../../../components/Button";
import EmptyState from "../../../components/EmptyState/EmptyState";
import Loading from "../../../components/Loading";
import DataTable from "../../../components/DataTable";
import Pill from "../../../components/Pill";
import GDDKDynamicModal from "../../../components/Modal/GDDKDynamicModal";
import ModalConfirmation from "../../../components/Modal/ModalConfirmation";
import { API_PATCH, API_PUT, API_EXPORT } from "../../../helpers/api";
import { setNotification } from "../../../helpers/notification";
import moment from "moment";
import { monthsAndResourceTypes } from "../../../constants/translations";
import { resourceTypes } from "../../../constants/selects";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";

const ProductionDataGDDKOperations = ({ t }) => {
  const dispatch = useDispatch();
  const selectedOrganization = useSelector(
    (state) => state.organizations.selectedOrganization
  );
  const {
    filterStation,
    filterResourceType,
    filterGDDKPeriod,
    activeList,
    activeLoading,
    filterHistoryResourceType,
    filterHistoryGDDKPeriod,
    historyList,
    historyLoading,
  } = useSelector((state) => state.gddk);

  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [showGDDKModal, setShowGDDKModal] = useState(false);
  const [selectedGDDK, setSelectedGDDK] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activePage, setActivePage] = useState(0);

  const translations = monthsAndResourceTypes(t);

  const selectResourceType = resourceTypes(t);

  const activeGDDKListColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        const term = moment(row.term);
        return `${translations[term.month() + 1]} ${term.year()}`;
      },
    },
    {
      name: t("pages.default.certificates.table.durationOfUse"),
      selector: "durationOfUse",
      sortable: true,
      cell: (row) => {
        const currentDate = moment();
        const expireDate = moment(row.expireDate);
        return Math.round(
          moment.duration(expireDate.diff(currentDate)).asMonths()
        );
      },
    },
    {
      name: t("pages.default.gddkOperations.table.gddkPeriod"),
      selector: "gddkPeriod",
      sortable: true,
      cell: (row) => {
        const gddTerm = moment(row.gddkTerm);
        return `${translations[gddTerm.month() + 1]} ${gddTerm.year()}`;
      },
    },
    {
      name: t("pages.default.certificates.table.debtDocumentCount"),
      selector: "certifiableAmount",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertAmountToFrontend(row.certifiableAmount)}
        </div>
      ),
    },
    {
      name: t("pages.default.certificates.table.status"),
      selector: "gddkStatus",
      sortable: true,
      width: "180px",
      cell: (row) => (
        <Pill
          label={t("pages.default.gddkOperations.table.notCompleted")}
          type="important"
        />
      ),
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      width: "140px",
      cell: (row) => (
        <div style={{ display: "flex" }}>
          <Button
            title={t("ui.default.edit")}
            suffixIcon="edit"
            type="primary"
            onClick={() => handleShowGDDKModal(row, true)}
          />
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            type="secondary"
            onClick={() => handleShowGDDKModal(row, false)}
          />
        </div>
      ),
    },
  ];

  const gddkHistoryListColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        const term = moment(row.term);
        return `${translations[term.month() + 1]} ${term.year()}`;
      },
    },
    {
      name: t("pages.default.certificates.table.durationOfUse"),
      selector: "durationOfUse",
      sortable: true,
      cell: (row) => {
        const currentDate = moment();
        const expireDate = moment(row.expireDate);
        return Math.round(
          moment.duration(expireDate.diff(currentDate)).asMonths()
        );
      },
    },
    {
      name: t("pages.default.gddkOperations.table.gddkPeriod"),
      selector: "gddkPeriod",
      sortable: true,
      cell: (row) => {
        const gddTerm = moment(row.gddkTerm);
        return `${translations[gddTerm.month() + 1]} ${gddTerm.year()}`;
      },
    },
    {
      name: t("pages.default.certificates.table.gddkDocumentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertAmountToFrontend(row.amount)}
        </div>
      ),
    },
    {
      name: t("pages.default.gddkOperations.table.bProdcutionAmount"),
      selector: "bProdcutionAmount",
      sortable: true,
      cell: (row) => (
        <div>
          {row.certifiableAmount
            ? convertAmountToFrontend(row.certifiableAmount)
            : "-"}
        </div>
      ),
    },
    {
      name: t("pages.default.certificates.table.status"),
      selector: "gddkStatus",
      sortable: true,
      cell: (row) => (
        <Pill label={t("ui.default.codes.SUCCESSFUL")} type="success" />
      ),
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      cell: (row) => (
        <div style={{ display: "flex" }}>
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            type="secondary"
            onClick={() => handleShowGDDKModal(row, false)}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    filterActiveGDDKList();
    filterGDDKHistoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectStation = (station) => {
    dispatch(setGDDKFilterStation(station));
  };

  const filterChange = (e) => {
    switch (e.target.name) {
      case "gddkPeriod":
        dispatch(setGDDKFilterGDDKPeriod(e.target.value.replace("-", "")));
        break;
      case "resourceType":
        dispatch(setGDDKFilterResourceType(e.target.value));
        break;
      case "historyGDDKPeriod":
        dispatch(
          setHistoryGDDKFilterGDDKPeriod(e.target.value.replace("-", ""))
        );
        break;
      case "historyResourceType":
        dispatch(setHistoryGDDKFilterResourceType(e.target.value));
        break;
      default:
        break;
    }
  };

  const filterActiveGDDKList = () => {
    dispatch(
      getActiveGDDKListRequest({
        organizationId:
          selectedOrganization && Object.keys(selectedOrganization).length > 0
            ? selectedOrganization.organizationId
            : "",
        stationId: filterStation ? filterStation.stationId : "",
        resourceType: filterResourceType === "-" ? "" : filterResourceType,
        term: filterGDDKPeriod,
      })
    );
  };

  const filterGDDKHistoryList = () => {
    dispatch(
      getGDDKHistoryListRequest({
        organizationId:
          selectedOrganization && Object.keys(selectedOrganization).length > 0
            ? selectedOrganization.organizationId
            : "",
        stationId: filterStation ? filterStation.stationId : "",
        resourceType:
          filterHistoryResourceType === "-" ? "" : filterHistoryResourceType,
        term: filterHistoryGDDKPeriod,
      })
    );
  };

  const handleStartGDDKEngineCLick = async () => {
    setLoadingRequest(true);
    try {
      await API_PATCH("production-data");
      setLoadingRequest(false);
      setShowModalConfirmation(false);
      setNotification(
        "success",
        t("pages.default.gddkOperations.startGDDKEngine.success")
      );
      filterActiveGDDKList();
      filterGDDKHistoryList();
    } catch (err) {
      setLoadingRequest(false);
      setNotification("error", err.response.data.message);
    }
  };

  const handleShowGDDKModal = (row, editModeValue) => {
    setSelectedGDDK(row);
    setShowGDDKModal(true);
    editMode !== editModeValue && setEditMode(editModeValue);
  };

  const downloadCommitmentStatus = async () => {
    setExportLoading(true);
    try {
      const res = await API_EXPORT("/production-data-admin/export/excel", {
        organizationId:
          selectedOrganization && Object.keys(selectedOrganization).length > 0
            ? selectedOrganization.organizationId
            : "",
        stationId: filterStation ? filterStation.stationId : "",
        resourceType:
          filterHistoryResourceType === "-" ? "" : filterHistoryResourceType,
        term: filterHistoryGDDKPeriod ? filterHistoryGDDKPeriod : null,
        lang: window.localStorage.i18nextLng,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };

  const handleSaveClick = async (id, gddkDesc) => {
    setLoadingRequest(true);
    try {
      await API_PUT(`production-data/${id}`, { description: gddkDesc });
      setLoadingRequest(false);
      setShowGDDKModal(false);
      setNotification(
        "success",
        t("pages.default.gddkOperations.updateSuccess")
      );
      filterActiveGDDKList();
    } catch (err) {
      setLoadingRequest(false);
      setNotification("error", err.response.data.message);
    }
  };

  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    dispatch(
      getGDDKHistoryListRequest({
        organizationId:
          selectedOrganization && Object.keys(selectedOrganization).length > 0
            ? selectedOrganization.organizationId
            : "",
        stationId: filterStation ? filterStation.stationId : "",
        resourceType:
          filterHistoryResourceType === "-" ? "" : filterHistoryResourceType,
        term: filterHistoryGDDKPeriod,
        page: activePage,
        selector: selector,
        direction: direction,
      })
    );
  };

  return (
    <>
      <div className="km__p--card">
        <div className="km__p--gddk-header">
          <div className="km__u--text_title">
            {t("pages.default.gddkOperations.activeGDDKTitle")}
          </div>
        </div>
        <div className="km__p--gddk-filter-container">
          <ComboBox t={t} />
          <ComboBoxStation
            t={t}
            selectedStation={filterStation}
            selectStation={selectStation}
          />
          <Select
            options={selectResourceType}
            name="resourceType"
            value={filterResourceType}
            onChange={filterChange}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.gddkOperations.filters.allGDDKPeriods"
            )}
            name="gddkPeriod"
            value={filterGDDKPeriod}
            onChange={filterChange}
          />
          <Button
            label={t("ui.default.filter")}
            onClick={filterActiveGDDKList}
          />
          <Button
            type="secondary"
            suffixIcon="refresh-ccw"
            label={t("pages.default.gddkOperations.startGDDKEngine.title")}
            onClick={() => setShowModalConfirmation(true)}
          />
        </div>

        {!activeLoading && (
          <DataTable
            columns={activeGDDKListColumns}
            data={activeList}
            rowKey="id"
            pagination
            paginationPerPage={10}
          />
        )}
        {activeList.length === 0 && !activeLoading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.gddkOperations.activeNotFound")}
          />
        )}
        {activeLoading && (
          <Loading text={t("pages.default.gddkOperations.activeLoading")} />
        )}
      </div>

      <div className="km__p--card">
        <div className="km__p--gddk-header">
          <div className="km__u--text_title">
            {t("pages.default.gddkOperations.historyGDDKTitle")}
          </div>
        </div>
        <div className="km__p--gddk-filter-container">
          <ComboBox t={t} />
          <ComboBoxStation
            t={t}
            selectedStation={filterStation}
            selectStation={selectStation}
          />
          <Select
            options={selectResourceType}
            name="historyResourceType"
            value={filterHistoryResourceType}
            onChange={filterChange}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.gddkOperations.filters.allGDDKPeriods"
            )}
            name="historyGDDKPeriod"
            value={filterHistoryGDDKPeriod}
            onChange={filterChange}
          />
          <Button
            label={t("ui.default.filter")}
            onClick={filterGDDKHistoryList}
          />
          <Button
            type="secondary"
            label={t("ui.default.download")}
            suffixIcon="download"
            onClick={downloadCommitmentStatus}
            disabled={exportLoading}
            id="c-download"
          />
        </div>

        {!historyLoading && (
          <DataTableServerSideRendering
            columns={gddkHistoryListColumns}
            data={historyList?.dataList}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={historyList?.total?.totalPageCount}
            handleFetchData={handleClick}
          />
        )}
        {historyList && historyList.length === 0 && !historyLoading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.gddkOperations.historyNotFound")}
          />
        )}
        {historyLoading && (
          <Loading text={t("pages.default.gddkOperations.historyLoading")} />
        )}
      </div>

      <ModalConfirmation
        header={t("pages.default.gddkOperations.startGDDKEngine.title")}
        body={t("pages.default.gddkOperations.startGDDKEngine.body")}
        isOpen={showModalConfirmation}
        toggle={() => setShowModalConfirmation(false)}
        approve={handleStartGDDKEngineCLick}
        loading={loadingRequest}
      />

      <GDDKDynamicModal
        t={t}
        isOpen={showGDDKModal}
        toggle={() => setShowGDDKModal(false)}
        selectedGDDK={selectedGDDK}
        editMode={editMode}
        editOnClick={handleSaveClick}
        loading={loadingRequest}
      />
    </>
  );
};

export default ProductionDataGDDKOperations;
